/**
 * @file useFloatingPanelStore
 * @description 资源区面板相关的状态管理
 * @author huangxuanmin
 */
import {create} from 'zustand';
import {AssetTypeValue, type AssetType} from '@/services/assets/interface';

export enum PanelState {
    Expanded = 'expanded',
    Collapsed = 'collapsed',
    Mini = 'mini',
}

export enum PanelType {
    // 分镜资源
    Storyboard = 'storyboard',
    // 主体资产
    Assets = 'assets',
}

export enum StoryboardTab {
    /** 参考 */
    Reference = 'reference',
    /** 图片 */
    Image = 'image',
    /** 视频 */
    Video = 'video',
    /** 音频 */
    Audio = 'audio',
}

export type PanelTab = StoryboardTab | AssetType;

export interface TabItem {
    key: PanelTab;
    label: string;
}

export type Tabs = TabItem[];

export interface FloatingPanelState {
    /** 面板展开状态： 展开｜缩略｜最小化 */
    panelState: PanelState;
    /** 面板类型：分镜｜资产 */
    panelType: PanelType;
    /** 当前选中的标签页 */
    currentTab: PanelTab;
    currentLabel: TabItem['label'];
    /** 隐藏分镜「参考」tab */
    hideStoryboardReferenceTab: boolean;
}

export interface FloatingPanelActions {
    /** 设置面板展开状态 */
    setPanelState: (panelState: PanelState) => void;
    /** 设置面板类型 */
    setPanelType: (panelType: PanelType) => void;
    /** 设置当前标签页 */
    setCurrentTab: (currentTab: PanelTab) => void;
    /** 设置是否隐藏分镜「参考」tab */
    setHideStoryboardReferenceTab: (hide: boolean) => void;
}

export const FLOATING_PANEL_TABS: {[key in PanelType]: Tabs} = {
    [PanelType.Storyboard]: [
        {key: StoryboardTab.Reference, label: '参考'},
        {key: StoryboardTab.Image, label: '图片'},
        {key: StoryboardTab.Video, label: '视频'},
        {key: StoryboardTab.Audio, label: '音频'},
    ],
    [PanelType.Assets]: [
        {key: AssetTypeValue.CHARACTER, label: '角色'},
        {key: AssetTypeValue.SCENE, label: '场景'},
        {key: AssetTypeValue.PROP, label: '道具'},
    ],
};

export const FLOATING_PANEL_TITLES: {[key in PanelType]: string} = {
    [PanelType.Storyboard]: '分镜素材',
    [PanelType.Assets]: '主体资产',
};

/** 按链路取可见 tab：参考生链路下过滤掉分镜「参考」tab */
export const getVisibleTabs = (panelType: PanelType, hideStoryboardReferenceTab: boolean): Tabs => {
    const tabs = FLOATING_PANEL_TABS[panelType];
    if (panelType === PanelType.Storyboard && hideStoryboardReferenceTab) {
        return tabs.filter((tab) => tab.key !== StoryboardTab.Reference);
    }
    return tabs;
};

const getDefaultTab = (panelType: PanelType, hideStoryboardReferenceTab = false): TabItem =>
    getVisibleTabs(panelType, hideStoryboardReferenceTab)?.[0];
export const useFloatingPanelStore = create<FloatingPanelState & FloatingPanelActions>((set) => ({
    panelState: PanelState.Collapsed,
    panelType: PanelType.Storyboard,
    currentTab: getDefaultTab(PanelType.Storyboard, true)?.key,
    currentLabel: getDefaultTab(PanelType.Storyboard, true)?.label,
    hideStoryboardReferenceTab: true,
    setPanelState: (panelState) => set({panelState}),
    setPanelType: (panelType) =>
        set((state) =>
            state.panelType === panelType
                ? state
                : {
                      panelType,
                      currentTab: getDefaultTab(panelType, state.hideStoryboardReferenceTab)?.key,
                  },
        ),
    setCurrentTab: (currentTab) =>
        set((state) => {
            const tabs = getVisibleTabs(state.panelType, state.hideStoryboardReferenceTab);
            const matchedTab = tabs.find((tab) => tab.key === currentTab);

            if (!matchedTab?.key) {
                return state;
            }

            return {currentTab, currentLabel: matchedTab.label};
        }),
    setHideStoryboardReferenceTab: (hideStoryboardReferenceTab) =>
        set((state) =>
            state.hideStoryboardReferenceTab === hideStoryboardReferenceTab
                ? state
                : {hideStoryboardReferenceTab},
        ),
}));
